import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh';

export const formatDateOnRequest = (date: string | undefined | null): string | undefined => {
  if (!date) return undefined; // 이 줄 추가!

  const dateObj = dayjs(date);
  if (dateObj.isValid()) {
    return dateObj.format('YYYY-MM-DD');
  } else {
    return undefined;
  }
};

export const formatRequestToDate = (date: string | undefined | null, replace?: string): string => {
  const dateObj = dayjs(date);
  if (dateObj.isValid()) {
    return dateObj.format('MMM DD YYYY').toUpperCase();
  } else {
    return replace ?? 'Add dates';
  }
};

export const formatRequestToDateTime = (date: string | undefined | null, replace?: string): string => {
  const dateObj = dayjs(date);
  if (dateObj.isValid()) {
    return dateObj.format('MMM DD YYYY HH:mm').toUpperCase();
  } else {
    return replace ?? 'Add dates';
  }
};

export const formatStringToDate = (date: string | undefined | null): Date | null => {
  if (date && dayjs(date).isValid()) {
    return dayjs(date).toDate();
  } else {
    return null;
  }
};

export const formatDateToString = (date: Date | null): string => {
  if (date) {
    return dayjs(date).format('MMM DD YYYY').toUpperCase();
  } else {
    return '';
  }
};

export const formatPrice = (price: number | null, language?: string): string => {
  // 언어별 금액 표시
  // - 기본(en 등): M, K 단위 (예: 10M, 500K)
  // - 중국어(zh): 만 단위 (예: 230000 -> 23万)
  // - 베트남어(vi): 전체 금액 표시 (예: 230000 -> 230,000)

  if (!price || Number.isNaN(price)) {
    return '-';
  }

  if (language === 'zh' || language === 'ja') {
    // 10,000원 단위로 万 표시
    const wan = price / 10000;
    // 정수/소수 여부에 따라 포맷
    const formatted = Number.isInteger(wan) ? wan.toString() : wan.toFixed(1);
    return `${formatted}万`;
  }

  if (language === 'vi') {
    // 전체 금액을 천 단위 구분 기호와 함께 표시
    return price.toLocaleString('vi-VN');
  }

  // 기본: M, K 단위
  if (price >= 1_000_000) {
    const m = price / 1_000_000;
    return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
  }
  if (price >= 1_000) {
    const k = price / 1_000;
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return `${price}`;
};

export const formatPriceOnlyM = (price: number | null, language?: string): string => {
  if (!price) {
    return '-';
  }

  // 중국어/일본어인 경우 万(만) 단위로 표시
  if (language === 'zh' || language === 'ja') {
    return `${price / 10000}万`;
  }

  return `${price / 1000000}M`;
};

export const formatPriceOnlyK = (price: number | null, language?: string): string => {
  if (!price) {
    return '-';
  }

  // 중국어/일본어인 경우 万(만) 단위로 표시
  if (language === 'zh' || language === 'ja') {
    if (price >= 10000) {
      return `${price / 10000}万`;
    }
    return `${price.toLocaleString()}`;
  }

  return `${price / 1000}K`;
};

/**
 * @description 카드 종류를 표시용으로 변환하는 함수
 * @param cardKind 카드 종류 (VISA, MASTERCARD 등)
 * @returns 변환된 카드 종류 문자열
 */
export const formatCardKind = (cardKind: string): string => {
  return cardKind.charAt(0) + cardKind.slice(1).toLowerCase();
};

/**
 * @description 현재 언어에 맞게 날짜를 현지화하여 표시하는 함수
 * @param date 날짜 문자열
 * @param currentLanguage 현재 언어 ('en' | 'vi' | 'zh')
 * @returns 현지화된 날짜 문자열
 */
export const formatLocalizedDate = (date: string | undefined | null, currentLanguage: string): string => {
  if (!date) return '';

  const dateObj = dayjs(date);
  if (!dateObj.isValid()) return '';

  // 언어별 날짜 포맷
  switch (currentLanguage) {
    case 'zh':
    case 'ja':
      return dateObj.format('YYYY年M月D日');
    case 'vi':
      return dateObj.format('DD/MM/YYYY');
    case 'en':
    default:
      return dateObj.format('MMM DD YYYY').toUpperCase();
  }
};

/**
 * @description 계약 기간을 현지화하여 표시하는 함수
 * @param months 월 수
 * @param monthsText 현지화된 "Months" 텍스트
 * @returns 현지화된 계약 기간 문자열
 */
export const formatLocalizedPeriod = (months: number, monthsText: string): string => {
  return `${months} ${monthsText}`;
};

/**
 * @description 리스팅 카드 전용 금액 포맷 함수
 * 언어별로 다른 표기 방식을 적용:
 * - 영어/베트남어: K/M 축약 단위 (e.g. 500K, 2M)
 * - 중국어/일본어: 천 단위 구분 기호 포함 전체 숫자 (e.g. 500,000, 2,000,000)
 */
export const formatListingPrice = (price: number | null, language?: string): string => {
  if (!price || Number.isNaN(price)) return '-';

  if (language === 'zh' || language === 'ja') {
    return price.toLocaleString('en-US');
  }

  if (price >= 1_000_000) {
    const m = price / 1_000_000;
    return `${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (price >= 1_000) {
    const k = price / 1_000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return `${price}`;
};

export const calculateCommission = (memberContractPeriod: number, roomType: string): number => {
  if (roomType === 'GOSHIWON') {
    return memberContractPeriod >= 5 ? 100_000 : 50_000;
  }
  return memberContractPeriod >= 5 ? 150_000 : 100_000;
};
